import React, { useEffect, useState } from "react";
import { api } from "@service/api";
import { IRaffleCategoryDTO } from "@hooks/dtos/raffles/IRaffleCategoryDTO";
import { RaffleCardSample } from "@components/cards/raffle/RaffleCard";
import {
  IRafflePublicListDTO,
  RaffleStatusTypes,
} from "@hooks/dtos/raffles/IRaffleGeneralDTO";
import { RaffleStatusEnum } from "@hooks/enums/raffle";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { FaTrashAlt } from "react-icons/fa";
import { LoadingSkeleton } from "@components/skeleton-loading";
import { Link } from "react-router-dom";

interface IFilters {
  status: RaffleStatusTypes;
  order: "DESC" | "ASC";
}

export const RaffleSlider: React.FC = () => {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  // --- Filters --- //
  const [filters, setFilters] = useState<IFilters>({
    status: RaffleStatusEnum.ACTIVE,
    order: "DESC",
  });
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [categories, setCategories] = useState<IRaffleCategoryDTO[]>([]);

  // Control Things & General Configs
  const [isLoading, setIsLoading] = useState<{ raffles: boolean }>({
    raffles: true,
  });
  const [raffles, setRaffles] = useState<IRafflePublicListDTO[]>([]);

  const handleCategoryList = async () => {
    try {
      const params = `?`;
      const result = await api.get(`/raffle/categories${params}`);
      
      setCategories(result.data);
    } catch (err: any) {
      console.error(err);
    }
  };

  async function handleCampaignList() {
    try {
      setIsLoading((prev) => ({
        ...prev,
        raffles: true,
      }));

      const data = {
        page: 1,
        take: 10,
        order: filters.order,
        status: filters.status,
        categories: selectedCategories,
        // searchCampaign,
        // startAt,
        // endAt
      };
      const result = await api.post(`/raffleClient/default/list`, data);

      setRaffles(result.data.result);

      setIsLoading((prev) => ({
        ...prev,
        raffles: false,
      }));
    } catch (err: any) {
      pushToastify({
        message: err?.response?.data.message ?? "",
        type: "error",
        theme: "dark",
      });
      setIsLoading((prev) => ({
        ...prev,
        raffles: false,
      }));
    }
  }

  const handleSelectCategory = (category: number) => {
    setSelectedCategories(
      (prev) =>
        prev.includes(category)
          ? prev.filter((c) => c !== category) // remove if exists
          : [...prev, category], // add if don't
    );
  };

  useEffect(() => {
    handleCategoryList();
    handleCampaignList();
  }, []);

  useEffect(() => {
    handleCampaignList();
  }, [selectedCategories]);

  return (
    <section
      className="bg-light pt-5 pb-4 px-3"
      style={{
        background:
          "linear-gradient(160deg, #fff 0%, #ECF4FF 60%, #D6E9FF 100%)",
        overflow: "hidden",
      }}
    >
      <div className="container px-3 px-md-4 px-lg-0">
        {/* HEADER */}
        <div className="mb-4">
          <small className="text-warning fw-bold fs-5">🎟️ Campanhas</small>
          <h2 className="fw-bold mb-0">Mais Recentes</h2>
        </div>

        {/* FILTERS */}
        <div className="d-flex flex-wrap mb-4 pb-2 gap-2">
          {/* Clear the Filters */}
          <button
            key={0}
            onClick={() => setSelectedCategories([])}
            className={`btn btn-sm btn-outline-danger rounded-pill`}
            style={{ whiteSpace: "nowrap" }}
            disabled={isLoading.raffles}
          >
            <FaTrashAlt /> Limpar
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleSelectCategory(category.id)}
              className={`btn btn-sm rounded-pill ${
                selectedCategories.length > 0 &&
                selectedCategories.includes(category.id)
                  ? "btn-warning text-white"
                  : "btn-outline-secondary"
              }`}
              style={{ whiteSpace: "nowrap" }}
              disabled={isLoading.raffles}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* SLIDER */}
        {isLoading.raffles ? (
          <LoadingSkeleton count={4} />
        ) : (
          <>
            {raffles.length === 0 ? (
              <div className="bg-white border rounded-4 text-center py-5 px-3">
                <div className="fw-bold mb-1">
                  Nenhuma campanha encontrada.
                </div>
                <div className="text-muted">
                  Não encontramos campanhas recentes para os filtros
                  selecionados.
                </div>
              </div>
            ) : (
              <div
                className="d-flex overflow-auto"
                style={{
                  gap: "1rem",
                  padding: "0.5rem 0.75rem 0.75rem",
                }}
              >
                {raffles.map((raffle, index) => (
                  <RaffleCardSample
                    raffle={raffle}
                    index={index}
                    key={raffle.key}
                  />
                ))}
              </div>
            )}

            {/* Buttons */}
            <div className="row justify-content-center mt-3">
              <div className="col-4 text-center">
                <Link
                  to="/campaign/default/list"
                  className="btn btn-primary fw-semibold w-100 px-4"
                  style={{ borderRadius: "50px" }}
                >
                  Ver Campanhas
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
